import { goClient } from '../utils/httpClientTs';
import { goRequest } from '../utils/httpClientTs';
import type {
  CheckBatchDetailResponse,
  CheckBatchesListResponse,
  CheckPaymentDTO,
  CreateCheckBatchResponse,
  PaginatedMeta,
  PayPalTransactionsResponse,
  StripeChargesResponse,
  StripePayoutDetailResponse,
  StripePayoutsListResponse,
  PendingChecksBySourceResponse,
} from '../types/financePayments';

export const getStripeCharges = async (params: {
  page: number;
  page_size: number;
  source?: string;
}): Promise<StripeChargesResponse> => {
  let url = `/finance/payments/stripe/charges?page=${params.page}&page_size=${params.page_size}`;
  if (params.source) {
    url += `&source=${encodeURIComponent(params.source)}`;
  }
  return goRequest<StripeChargesResponse>({ url, method: 'GET' });
};

export const getPayPalTransactions = async (params: {
  page: number;
  page_size: number;
}): Promise<PayPalTransactionsResponse> => {
  const url = `/finance/payments/paypal/transactions?page=${params.page}&page_size=${params.page_size}`;
  return goRequest<PayPalTransactionsResponse>({ url, method: 'GET' });
};

export const getPayouts = async (params: {
  page: number;
  page_size: number;
}): Promise<StripePayoutsListResponse> => {
  const url = `/finance/payments/payouts?page=${params.page}&page_size=${params.page_size}`;
  return goRequest<StripePayoutsListResponse>({ url, method: 'GET' });
};

export const getPayoutDetail = async (
  payoutId: string
): Promise<StripePayoutDetailResponse> => {
  return goRequest<StripePayoutDetailResponse>({
    url: `/finance/payments/payouts/${encodeURIComponent(payoutId)}`,
    method: 'GET',
  });
};

export const getCheckPayments = async (params: {
  page: number;
  page_size: number;
  status?: string;
  /** When true, only checks not assigned to a batch (for pick list). */
  unbatched_only?: boolean;
}): Promise<{ data: CheckPaymentDTO[]; pagination: PaginatedMeta }> => {
  let url = `/finance/payments/checks?page=${params.page}&page_size=${params.page_size}`;
  if (params.status) {
    url += `&status=${encodeURIComponent(params.status)}`;
  }
  if (params.unbatched_only) {
    url += '&unbatched_only=1';
  }
  return goRequest({ url, method: 'GET' });
};

export const getPendingChecksBySource =
  async (): Promise<PendingChecksBySourceResponse> => {
    return goRequest<PendingChecksBySourceResponse>({
      url: '/finance/payments/checks/pending-by-source',
      method: 'GET',
    });
  };

export const getCheckBatches = async (params: {
  page: number;
  page_size: number;
  status?: string;
}): Promise<CheckBatchesListResponse> => {
  let url = `/finance/payments/check-batches?page=${params.page}&page_size=${params.page_size}`;
  if (params.status) {
    url += `&status=${encodeURIComponent(params.status)}`;
  }
  return goRequest<CheckBatchesListResponse>({ url, method: 'GET' });
};

export const createCheckBatch = async (): Promise<CreateCheckBatchResponse> => {
  return goRequest<CreateCheckBatchResponse>({
    url: '/finance/payments/check-batches',
    method: 'POST',
    data: {},
  });
};

export const getCheckBatch = async (
  batchId: string
): Promise<CheckBatchDetailResponse> => {
  return goRequest<CheckBatchDetailResponse>({
    url: `/finance/payments/check-batches/${encodeURIComponent(batchId)}`,
    method: 'GET',
  });
};

export const addChecksToBatch = async (
  batchId: string,
  checkPaymentIds: string[]
): Promise<void> => {
  await goRequest({
    url: `/finance/payments/check-batches/${encodeURIComponent(batchId)}/checks`,
    method: 'POST',
    data: { check_payment_ids: checkPaymentIds },
  });
};

export const removeCheckFromBatch = async (
  batchId: string,
  checkPaymentId: string
): Promise<void> => {
  await goRequest({
    url: `/finance/payments/check-batches/${encodeURIComponent(batchId)}/checks/${encodeURIComponent(checkPaymentId)}`,
    method: 'DELETE',
  });
};

export const finalizeCheckBatch = async (batchId: string): Promise<void> => {
  await goRequest({
    url: `/finance/payments/check-batches/${encodeURIComponent(batchId)}/finalize`,
    method: 'POST',
    data: {},
  });
};

export const downloadCheckBatchPdf = async (batchId: string): Promise<void> => {
  const res = await goClient.get(
    `/finance/payments/check-batches/${encodeURIComponent(batchId)}/print`,
    { responseType: 'blob' }
  );
  const blob = new Blob([res.data], { type: 'application/pdf' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `check-batch-${batchId}.pdf`;
  a.click();
  window.URL.revokeObjectURL(url);
};

export const downloadPayoutDepositReport = async (
  payoutId: string
): Promise<void> => {
  const res = await goClient.get(
    `/finance/payments/payouts/${encodeURIComponent(payoutId)}/deposit-report`,
    { responseType: 'blob' }
  );
  const blob = new Blob([res.data], { type: 'application/pdf' });
  const href = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = `deposit-gl-stripe-${payoutId}.pdf`;
  a.click();
  window.URL.revokeObjectURL(href);
};

export const downloadCheckBatchDepositReport = async (
  batchId: string
): Promise<void> => {
  const res = await goClient.get(
    `/finance/payments/check-batches/${encodeURIComponent(batchId)}/deposit-report`,
    { responseType: 'blob' }
  );
  const blob = new Blob([res.data], { type: 'application/pdf' });
  const href = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = `deposit-gl-check-batch-${batchId}.pdf`;
  a.click();
  window.URL.revokeObjectURL(href);
};
